import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { useIsMobile } from '../../hooks/useResponsive';
import { useAuth } from '../../context/AuthContext';
import useAppStore from '../../store/appStore';
import { userData, schedule } from '../../data/mockData';
import {
  getPipelineValue,
  getNewLeadsStats,
  getActionRequiredCount,
} from '../../firebase/dashboard.service';
import {
  getUpcomingMeetings,
  getCalendlyStatus,
} from '../../firebase/meetings.service';
import DesktopDashboard from './DesktopDashboard';
import MobileDashboard from './MobileDashboard';

/**
 * Format a number as compact USD currency.
 * e.g. 1200000 → "$1.2M", 450000 → "$450K", 800 → "$800"
 */
const formatCurrency = (value) => {
  if (value >= 1_000_000) {
    const m = value / 1_000_000;
    return `$${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
  }
  if (value >= 1_000) {
    const k = value / 1_000;
    return `$${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}K`;
  }
  return `$${value}`;
};

/** Default empty stats shown while loading for the first time. */
const EMPTY_STATS = {
  pipelineValue: '--',
  pipelineChange: null,
  newLeadsToday: '--',
  newLeadsYesterday: '--',
  actionRequired: '--',
};

const DashboardContainer = () => {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const navigate = useNavigate();

  // ── Global store (single source of truth) ─────────────────
  const cachedMeetings = useAppStore((s) => s.upcomingMeetings);
  const cachedCalendly = useAppStore((s) => s.calendlyConnected);
  const setUpcomingMeetings = useAppStore((s) => s.setUpcomingMeetings);
  const setCalendlyConnected = useAppStore((s) => s.setCalendlyConnected);

  // ── Local UI state (never in global store) ────────────────
  const [expandedCard, setExpandedCard] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);
  const [meetingsLoading, setMeetingsLoading] = useState(!cachedMeetings);
  const [dashboardStats, setDashboardStats] = useState(EMPTY_STATS);
  const [meetings, setMeetings] = useState(cachedMeetings || []);
  const [calendlyConnected, setCalendlyConnectedLocal] = useState(cachedCalendly);

  // ── Fetch all 3 metrics in parallel ───────────────────────
  const fetchDashboardStats = useCallback(async () => {
    if (!user?.uid) return;

    setStatsLoading(true);
    setStatsError(null);

    try {
      const [pipeline, leads, action] = await Promise.all([
        getPipelineValue(user.uid),
        getNewLeadsStats(user.uid),
        getActionRequiredCount(user.uid),
      ]);

      // Compute pipeline change percentage
      // (not applicable — no historical data to compare against)
      // We show property count instead.
      const pipelineChange = pipeline.count > 0
        ? `${pipeline.count} ${pipeline.count === 1 ? 'property' : 'properties'}`
        : null;

      const newStats = {
        pipelineValue: formatCurrency(pipeline.total),
        pipelineChange,
        newLeadsToday: leads.today,
        newLeadsYesterday: leads.yesterday,
        actionRequired: action.count,
      };

      return newStats;
    } catch (err) {
      console.error('[dashboard] Failed to fetch stats:', err);
      setStatsError('Failed to load dashboard metrics');
      return null;
    } finally {
      setStatsLoading(false);
    }
  }, [user?.uid]);

  // ── Fetch meetings + Calendly status ──────────────────────
  const fetchMeetings = useCallback(async () => {
    if (!user?.uid) return;

    setMeetingsLoading(true);
    try {
      const [status, upcomingMeetings] = await Promise.all([
        getCalendlyStatus(user.uid),
        getUpcomingMeetings(user.uid, 5).catch(() => []),
      ]);

      setCalendlyConnectedLocal(status.connected);
      setCalendlyConnected(status.connected);
      setMeetings(upcomingMeetings);
      setUpcomingMeetings(upcomingMeetings);
      return { meetings: upcomingMeetings, calendlyConnected: status.connected };
    } catch (err) {
      console.error('[dashboard] Failed to fetch meetings:', err);
      setMeetings([]);
      return null;
    } finally {
      setMeetingsLoading(false);
    }
  }, [user?.uid]);

  // ── Data loading (always fetch fresh — no TTL cache) ──────
  useEffect(() => {
    if (!user?.uid) return;

    // Fetch stats and meetings in parallel
    fetchDashboardStats().then((newStats) => {
      if (newStats) {
        setDashboardStats(newStats);
      }
    });
    fetchMeetings();
  }, [user?.uid]); // eslint-disable-line

  const toggleCard = (id) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  const handleLogout = () => {
    const auth = getAuth();
    signOut(auth)
      .then(() => {
        // Clear global cache on logout
        localStorage.removeItem('uid');
        useAppStore.getState().reset();
        navigate('/login');
      })
      .catch((error) => {
        console.error('Logout failed:', error);
      });
  };

  if (isMobile) {
    return (
      <MobileDashboard
        userData={userData}
        dashboardStats={dashboardStats}
        statsLoading={statsLoading}
        statsError={statsError}
        schedule={schedule}
        meetings={meetings}
        meetingsLoading={meetingsLoading}
        calendlyConnected={calendlyConnected}
        expandedCard={expandedCard}
        toggleCard={toggleCard}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <DesktopDashboard
      userData={userData}
      dashboardStats={dashboardStats}
      statsLoading={statsLoading}
      statsError={statsError}
      schedule={schedule}
      meetings={meetings}
      meetingsLoading={meetingsLoading}
      calendlyConnected={calendlyConnected}
      onLogout={handleLogout}
    />
  );
};

export default DashboardContainer;
